/* eslint-disable max-classes-per-file */
import React, { Component } from 'react'
import { observer } from 'mobx-react'
import { makeObservable } from 'mobx'
import * as R from 'ramda'
import { cloneDeep } from 'lodash'
import surveyApi from '../API/Survey'
import { unescapeHtmlTags } from '../utils/helpers'
import SurveyBuilderQuestionAccordion from './Presentation/components/SurveyBuilderQuestionAccordion'

import '../assets/css/pagequestion.css'
import { trackBuilderQuestionMenuAction, trackBuilderLogicEdit } from '../utils/MixpanelManager'

const questionMappingTypeOptions = Object.freeze({
    // keys = to, array = from
    RD: ['RD', 'SB', 'TB'],
    SB: ['SB', 'RD', 'TB'],
    TB: ['TB'],
    TA: ['TA', 'TX'],
    TX: ['TX', 'TA'],
    UV: ['UV', 'TL'],
    TL: ['TL', 'UV'],
    CH: ['CH'],
    OD: ['OD'],
    NF: ['NF'],
    I: ['I', 'NF'],
    PC: ['PC'],
    DT: ['DT'],
})

const inheritsAllowed = Object.freeze({
    // All questions can inherit from same question types
    // TB questions can only inherit to its rows
    from: ['RD', 'CH', 'SB'],
    to: ['RD', 'CH', 'SB', 'TL', 'TB', 'OD', 'PC', 'SB'],
})
@observer
class PageQuestions extends Component {
    constructor(props) {
        super(props)
        makeObservable(this)
    }

    getQuestionById(id, data = this.data) {
        const questions = data.pages.filter((page) => (page.hasOwnProperty('questions') ? page : null)).map((page) => page.questions.map((question) => {
            if (question.questionTypeCode === 'FM') {
                return question.optionSet.flatMap((formQuestion) => formQuestion.question)
            }
            return question
        }))

        const flattened = questions.flat(2)

        return flattened.find((question) => question.id === id)
    }

    setEditable = () => {
        const { question, store } = this.props
        store.survey.editable_question = question
        store.tabIndex = 2
    }

    setSkipLogic = () => {
        const {
            question,
            page,
            question_id,
            store,
        } = this.props

        if (this.isEditingDisabled()) return

        const pageIndex = store.survey.pages.findIndex((surveyPage) => (
            surveyPage.id === page.id
        ))

        trackBuilderQuestionMenuAction(store, { action: 'open logic menu', logic_type_menu: 'inherit' })
        question.page_index = pageIndex
        question.index = question_id
        store.survey.editable_question = question
        let skipLogic = question.hasSkipLogic(store)
        if (!skipLogic) {
            skipLogic = store.survey.newShowIf(question)
        }

        store.skipLogic = store.survey.copySkipLogic(JSON.parse(JSON.stringify(skipLogic)))
        store.previousSkipLogic = cloneDeep(store.survey.skipLogic)
        store.skipLogicConfig = {
            pageIndex,
            page,
            type: 'question',
            title: question.question.content.getTitleByLocale('shortTitle', store.surveyLocale, store.survey.metadata.defaultLocale),
            questionIndex: page.questions.findIndex((pq) => pq.id === question.id),
            question,
        }

        store.skipLogicModalVisible = true
    }

    pipeQuestion = () => {
        const {
            question,
            store,
            pageIndex,
        } = this.props

        if (store.survey.closed && !store.isExploriAdmin()) {
            return
        }

        const { survey } = store

        const pages = survey.pages.filter((p, i) => p.pageType === 'ST' && i <= pageIndex)
        const currentPage = pages[pageIndex]
        const currentQuestionIndex = currentPage.questions.findIndex((pq) => pq.id === question.id)

        const currentQuestionType = R.pathOr('', ['question', 'questionTypeCode'], question)
        const allowedQTypes = questionMappingTypeOptions[currentQuestionType]

        const pageQuestions = pages.reduce((arr, page, i) => (
            arr.concat(page.questions.map((q, questionIndex) => {
                // this is where possible parent questions are added to the options
                const questionType = q.question.questionTypeCode
                if (!allowedQTypes.includes(questionType)) return undefined
                if (q.isHidden) return undefined
                if (!(questionIndex < currentQuestionIndex || i < pageIndex)) return undefined
                if (questionType === 'TB' && currentQuestionType !== 'TB') {
                    return R.pathOr([], ['question', 'optionSetY', 'options'], q).map((option) => (
                        {
                            ...q,
                            id: `${q.id}_${option.id}`,
                            title: `${unescapeHtmlTags(survey.replaceMergeFields(q.questionTitle(store), store))} - ${option.getTitle(store.surveyLocale, store.survey.metadata.defaultLocale, q.question.content.initialLocale)}`,
                        }
                    ))
                }

                return q
            }).flat(1).filter((q) => !!q))
        ), [])

        const options = pageQuestions.map((pq) => ({
            value: pq.id,
            label: pq.title || unescapeHtmlTags(survey.replaceMergeFields(pq.questionTitle(store), store)),
        }))

        store.questionPipeSettings = {
            pageQuestions,
            targetQuestion: question,
            options,
            mappings: question.populateFrom,
        }
        store.showQuestionPipe = true
    }

    isEditingDisabled = () => {
        const {
            question,
            store,
        } = this.props

        return store.survey.closed || !question.canBeEdited
    }

    editQuestion = () => {
        const { question, store } = this.props

        if (this.isEditingDisabled() && !store.isExploriAdmin()) return

        if (store.survey.metadata.isRapidSurvey && ['Explori', 'Client'].includes(question.questionLevel)) {
            store.pendingExploriEdit = { cb: () => this.doEditQuestion() }
            store.exploriLiteEditWarning.questionLevel = question.questionLevel
            store.exploriLiteEditWarning.show = true
            return
        }

        this.doEditQuestion()
    }

    doEditQuestion = () => {
        const {
            question,
            page,
            store,
            pageIndex,
            question_id,
        } = this.props

        store.questionDetails = {
            action: 'editQuestion',
            prevQuestions: page.prev_questions,
            parentId: page.prev_questions.find((inherit) => inherit.value === question.parentId),
            oid: question.oid,
            pageQuestionId: question.id,
            survey: store.survey,
            tokenData: store.tokenData,
            questionId: question.questionId,
            questionIndex: question_id,
            usedInOtherSurveys: question.usedInOtherSurveys,
            usedInParentSurvey: question.usedInParentSurvey,
            pageIndex,
            smartGrid: question.question.questionTypeCode === 'SG' ? question.optionSetY : undefined,
        }

        store.showQuestionEditor = true
        store.skipLogicConfig = {
            questionIndex: question_id,
            pageIndex,
            question,
            onSave: (s, l) => question.updateSkipLogic(s, l),
        }
    }

    copyQuestion = () => {
        const { question, store, page } = this.props

        if (this.isEditingDisabled()) return
        trackBuilderQuestionMenuAction(store, { action: 'duplicate question' })
        page.copyQuestion(question, store)
    }

    toggleMandatory = () => {
        const { question, store } = this.props

        if (this.isEditingDisabled()) return

        if (question.deletable) {
            trackBuilderQuestionMenuAction(store, { action: 'toggle required', value: Boolean(!question.isMandatory) })
            question.isMandatory = !question.isMandatory
        }
    }

    toggleIsAiCodingDisabled = () => {
        const { question, store } = this.props
        console.log({a: question.isAiCodingDisabled})
        trackBuilderQuestionMenuAction(store, { action: 'toggle ai coding', value: Boolean(question.isAiCodingDisabled) })
        question.isAiCodingDisabled = !question.isAiCodingDisabled
    }

    toggleDisable = () => {
        const { store, question } = this.props

        if (this.isEditingDisabled() && !store.isExploriAdmin()) return

        store.questionToDisable = {
            question,
        }
        store.questionDisableConfirm = true
    }

    toggleIsEditable = () => {
        const { question, store } = this.props
        if ((!store.survey.closed && store.isMasterSuper()) || store.isExploriAdmin()) {
            trackBuilderQuestionMenuAction(store, { action: 'toggle lock question', value: Boolean(question.canBeEdited) })
            question.canBeEdited = !question.canBeEdited
        }
    }

    isRecommended = (questionId) => {
        const { store } = this.props
        const { survey } = store
        const { questionLibrary } = survey
        const { questionLibraryStore } = questionLibrary

        return questionLibraryStore.questionLibraryLists.get('recommended')
            .find((rq) => rq.questionId.toUpperCase() === questionId.toUpperCase())
    }

    removeQuestion = (e) => {
        const {
            page,
            question,
            question_id,
            store,
        } = this.props

        if (this.isEditingDisabled()) return

        if (store.tokenData.isAdmin || question.deletable || this.isRecommended(question.questionId)) {
            store.questionToDelete = {
                question,
                page,
                question_id,
            }
            store.questionDeleteConfirm = true
        }
        e.preventDefault()
    }

    setInheritQuestion = (obj) => {
        const { question, store } = this.props

        if (this.isEditingDisabled()) return

        if (question.parentId === obj.value) {
            return
        }

        if (question.parentId
                && !question.parentId.match(/^[0|-]*$/)
                && store.survey.inheritLogicExists(question.parentId)) {
            store.pendingParentChange = {
                cb: () => {
                    const options = Object.assign({}, question.getCurrentOptions(), {
                        parentId: obj.value || '00000000-0000-0000-0000-000000000000',
                    })
                    question.update(options)
                    question.parentId = obj.value
                    trackBuilderLogicEdit(store, {
                        action: obj.value ? 'add/edit' : 'delete',
                        logic_type: 'inherit',
                        type: 'question',
                        question_type: question.question.questionTypeCode,
                        source: 'menu',
                    })
                },
            }
            store.showInheritanceWarning = true
        } else {
            const options = Object.assign({}, question.getCurrentOptions(), {
                parentId: obj.value || '00000000-0000-0000-0000-000000000000',
            })
            question.update(options)
            question.parentId = obj.value
            trackBuilderLogicEdit(store, {
                action: obj.value ? 'add/edit' : 'delete',
                logic_type: 'inherit',
                type: 'question',
                question_type: question.question.questionTypeCode,
                source: 'menu',
            })
        }
    }

    toggleInherit = () => {
        const { question } = this.props

        if (this.isEditingDisabled()) return

        question.inheritVisible = !question.inheritVisible
    }

    test = () => {
        const { page, store } = this.props
        const { survey } = store
        survey.mouseDown = page
    }

    getSpinnerClass = () => {
        const { question, store } = this.props
        const { survey } = store
        let cssClass = ''

        if (question.clientId === 1) {
            cssClass = 'q_explori'
        } else if (question.clientId === survey.metadata.get('clientId') && question.oid < 6) {
            cssClass = 'q_client'
        } else {
            cssClass = 'q_product'
        }

        return cssClass
    }

    copyPQID = async () => {
        const { question, store } = this.props
        try {
            await window.navigator.clipboard.writeText(question.id)
            store.addNotification(null, 'PageQuestionId copied', 'success')
        } catch (e) {
            store.addNotification(null, 'Error copying pageQuestionId', 'error')
        }
    }

    copyQID = async () => {
        const { question, store } = this.props
        try {
            await window.navigator.clipboard.writeText(question.questionId)
            store.addNotification(null, 'QuestionId copied', 'success')
        } catch (e) {
            store.addNotification(null, 'Error copying questionId', 'error')
        }
    }

    checkRecommended = () => {
        const {
            question,
            store,
        } = this.props
        const { survey } = store

        const questionLists = R.pathOr(undefined, ['questionLibrary', 'questionLists'], survey)

        if (!questionLists || !questionLists.has('mandatory')) return false

        const recommendedQuestion = questionLists.get('mandatory').find((q) => q.questionId.toLowerCase() === question.questionId.toLowerCase() && q.isRecommended)

        return recommendedQuestion
    }

    checkDataImport = () => {
        const {
            question,
            store,
        } = this.props
        const { survey } = store

        const questionLists = R.pathOr(undefined, ['questionLibrary', 'questionLists'], survey)

        if (!questionLists || !questionLists.has('mandatory')) return false

        const dataImportQuestion = questionLists.get('mandatory').find((q) => q.questionId.toLowerCase() === question.questionId.toLowerCase() && q.isDataImport)

        return dataImportQuestion
    }

    findPageQuestionId = (questionId) => {
        const { data } = this
        const { pages } = data
        const questions = pages.map((p) => p.questions)
        const question = questions.flat().find((q) => (
            q && q.questionId.toLowerCase() === questionId.toLowerCase()
        ))
        return R.pathOr(questionId, ['id'], question)
    }

    getResponseOptionText = (pageQuestionId) => {
        let text = ''
        const question = this.getQuestionById(pageQuestionId)

        const prePopulated = this.prePopulatedData.has(pageQuestionId)

        if (question && ['TA', 'TX', 'RD'].includes(question.questionTypeCode) && (this.form_data.has(pageQuestionId) || prePopulated)) {
            const formData = prePopulated
                ? this.prePopulatedData.get(pageQuestionId)
                : this.form_data.get(pageQuestionId)

            if (['TA', 'TX'].includes(question.questionTypeCode)) {
                text = prePopulated
                    ? formData
                    : formData.data
            } else {
                const optionId = prePopulated
                    ? formData
                    : formData.data.get('value')
                try {
                    const questionOptions = question.optionSet.options
                    text = this.getOptionTitle(questionOptions.find((o) => (
                        o.id.toUpperCase() === optionId.toUpperCase()
                    )), question)
                } catch (e) {
                    console.error(e)
                }
            }
        }

        return text
    }

    reloadQuestion = async () => {
        const { question, page, store } = this.props
        const { survey } = store

        try {
            store.loading = true
            const body = {
                pageId: page.id,
                pageQuestionId: question.id,
                questionId: question.questionId,
            }
            await store.http.post(`/survey/${survey.id}/reload`, JSON.stringify(body))
            window.location.reload()
        } catch (e) {
            store.loading = false
            console.error(e)
            store.addNotification(null, 'Error reloading question', 'error')
        }
    }

    isFailed = (pageQuestion) => (
        R.pathOr(false, ['question', 'isFailed'], pageQuestion)
    )

    setObjectivesQuestion = () => {
        const { question, store } = this.props
        if (store.survey.metadata.sIObjectivesQuestionId !== question.questionId) {
            store.survey.metadata.sIObjectivesQuestionId = question.questionId
            surveyApi.updateSettings({
                sIObjectivesQuestionId: question.questionId,
            }, store)
        } else {
            store.survey.metadata.sIObjectivesQuestionId = '00000000-0000-0000-0000-000000000000'
            surveyApi.updateSettings({
                sIObjectivesQuestionId: '00000000-0000-0000-0000-000000000000',
            }, store)
        }
    }

    render() {
        const {
            question,
            page,
            store,
            pageIndex,
            provided,
            isDragging,
        } = this.props
        const { survey } = store
        const text = unescapeHtmlTags(
            survey.replaceMergeFields(question.questionTitle(store), store),
        )

        const inheritOptions = question
            .previousPageQuestions({ store, currentPriority: page.priority })
        const inheritParent = inheritOptions.find((option) => option.value === question.parentId)
        const inheritValue = inheritParent ? inheritParent.value : undefined
        let oid = 0
        if (question && question.question && question.question.metadata) {
            // question is undefined for a moment after it is dropped from questionLibrary
            oid = question.question.metadata.oid
        }

        return (
            <SurveyBuilderQuestionAccordion
                appStore={store}
                question={question}
                provided={provided}
                edit={this.editQuestion}
                copy={this.copyQuestion}
                skip={this.setSkipLogic}
                lock={this.toggleIsEditable}
                reloadQuestion={this.reloadQuestion}
                isFailed={this.isFailed(question)}
                remove={this.removeQuestion}
                makeMandatory={this.toggleMandatory}
                toggleIsAiCodingDisabled={this.toggleIsAiCodingDisabled}
                inherit={this.toggleInherit}
                setInherit={this.setInheritQuestion}
                showInherit={pageIndex > 0}
                inheritOptions={inheritOptions}
                inheritValue={inheritValue}
                inheritParent={inheritParent}
                inheritsAllowed={inheritsAllowed}
                mapsAllowed={questionMappingTypeOptions}
                pipeQuestion={this.pipeQuestion}
                isFullSize
                store={store}
                page={page}
                titleContent={text}
                disable={this.toggleDisable}
                oid={oid}
                isDragging={isDragging}
                setObjectivesQuestion={this.setObjectivesQuestion}
            />
        )
    }
}

export default PageQuestions
